import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { BRAND } from "../config/brand";
import { useLang } from "../i18n/LangContext";

export const Coach = () => {
  const { t } = useLang();
  const quali = t("coach.qualifications");

  return (
    <section
      id="coach"
      data-testid="section-coach"
      className="relative py-24 lg:py-32 bg-[#050505] overflow-hidden"
    >
      <div className="red-glow" style={{ top: "20%", left: "-25%", opacity: 0.35 }} />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={BRAND.expertPortrait}
                alt="LionPerformance Coach"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
              <div className="absolute inset-0 border border-white/10" />

              {/* Signature — bottom right */}
              <div className="absolute bottom-5 right-6 text-right pointer-events-none">
                <div
                  className="font-display font-semibold tracking-tight text-2xl lg:text-3xl leading-none"
                  style={{
                    background: "linear-gradient(135deg, #F6E27A 0%, #D4AF37 45%, #B8862B 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    textShadow: "0 2px 12px rgba(0,0,0,0.6)",
                    filter: "drop-shadow(0 1px 6px rgba(0,0,0,0.55))",
                  }}
                >
                  Marc Wiebeck
                </div>
                <div className="mt-1.5 text-[9px] font-bold uppercase tracking-[0.3em] text-gold/80">
                  Founder · Head Coach
                </div>
              </div>
            </div>

            {/* M.Sc. qualifications spelled out below the portrait */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="border border-white/10 bg-[#0a0a0a] px-4 py-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold mb-1.5">
                  M.Sc.
                </div>
                <div className="font-display text-white text-base lg:text-lg font-semibold leading-tight tracking-tight">
                  Sportpsychologie
                </div>
              </div>
              <div className="border border-white/10 bg-[#0a0a0a] px-4 py-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold mb-1.5">
                  M.Sc.
                </div>
                <div className="font-display text-white text-base lg:text-lg font-semibold leading-tight tracking-tight">
                  Sportwissenschaften
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 lg:pl-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-primary-red" />
              <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-400">
                {t("coach.eyebrow")}
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
              {t("coach.title")}
            </h2>
            <div className="mt-8 space-y-5 font-body text-base lg:text-lg text-zinc-300 leading-relaxed max-w-2xl">
              <p>{t("coach.p1")}</p>
              <p>{t("coach.p2")}</p>
            </div>

            {/* Micro-story block — identity/authority anchor */}
            <div
              data-testid="coach-story"
              className="mt-10 relative bg-[#0a0a0a] border-l-2 border-primary-red px-6 py-6 lg:px-8 lg:py-7 max-w-2xl"
            >
              <div className="absolute -top-3 left-6 lg:left-8 bg-black px-3 text-[10px] font-bold uppercase tracking-[0.28em] text-gold">
                Werdegang
              </div>
              <p className="font-body text-base lg:text-lg text-zinc-100 leading-relaxed italic">
                {t("coach.story")}
              </p>
            </div>

            <div className="gold-line my-10" />

            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-gold mb-6">
                {t("coach.quali")}
              </div>
              <ul className="grid sm:grid-cols-2 gap-4">
                {quali.map((q, i) => (
                  <li
                    key={i}
                    data-testid={`quali-${i}`}
                    className="flex items-start gap-3 group"
                  >
                    <span className="mt-1 w-5 h-5 rounded-full bg-primary-red/20 border border-primary-red flex items-center justify-center shrink-0 group-hover:bg-primary-red transition-colors">
                      <Check size={12} className="text-primary-red group-hover:text-white transition-colors" strokeWidth={3} />
                    </span>
                    <span className="font-body text-base lg:text-lg text-zinc-100 leading-snug">
                      {q}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
